import AsyncStorage from '@react-native-async-storage/async-storage';

const CACHE_KEYS = {
  ARTICLES: 'cache_articles',
  SHOPPING_LISTS: 'cache_shopping_lists',
  STORES: 'cache_stores',
  SYNC_QUEUE: 'sync_queue',
  LAST_SYNC: 'last_sync_timestamp',
  OFFLINE_CHANGES: 'offline_changes'
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 Minuten Cache-Dauer

/**
 * Cache Service für Offline-First Funktionalität
 */
class CacheService {
  /**
   * Speichert Daten im Cache mit Timestamp
   */
  async set(key, data) {
    try {
      const cacheEntry = {
        data,
        timestamp: Date.now()
      };
      await AsyncStorage.setItem(key, JSON.stringify(cacheEntry));
    } catch (error) {
      console.error(`❌ Cache save error for ${key}:`, error);
    }
  }

  /**
   * Holt Daten aus dem Cache
   * @param {string} key - Cache-Key
   * @param {number} maxAge - Maximales Alter in ms (optional)
   * @returns {Object|null} Gecachte Daten oder null
   */
  async get(key, maxAge = CACHE_DURATION) {
    try {
      const cached = await AsyncStorage.getItem(key);
      if (!cached) {
        return null;
      }

      const { data, timestamp } = JSON.parse(cached);
      const age = Date.now() - timestamp;

      if (maxAge && age > maxAge) {
        return null;
      }

      return data;
    } catch (error) {
      console.error(`❌ Cache read error for ${key}:`, error);
      return null;
    }
  }

  /**
   * Löscht einen Cache-Eintrag
   */
  async remove(key) {
    try {
      await AsyncStorage.removeItem(key);
    } catch (error) {
      console.error(`❌ Cache remove error for ${key}:`, error);
    }
  }

  /**
   * Löscht alle Cache-Einträge
   */
  async clear() {
    try {
      const keys = Object.values(CACHE_KEYS);
      await AsyncStorage.multiRemove(keys);
    } catch (error) {
      console.error('❌ Cache clear error:', error);
    }
  }

  /**
   * Speichert Artikel im Cache
   */
  async cacheArticles(articles) {
    await this.set(CACHE_KEYS.ARTICLES, articles);
  }

  /**
   * Holt Artikel aus dem Cache
   */
  async getCachedArticles() {
    return await this.get(CACHE_KEYS.ARTICLES);
  }

  /**
   * Speichert Einkaufslisten im Cache
   */
  async cacheShoppingLists(lists) {
    await this.set(CACHE_KEYS.SHOPPING_LISTS, lists);
  }

  /**
   * Holt Einkaufslisten aus dem Cache
   */
  async getCachedShoppingLists() {
    return await this.get(CACHE_KEYS.SHOPPING_LISTS);
  }

  /**
   * Speichert Läden im Cache
   */
  async cacheStores(stores) {
    await this.set(CACHE_KEYS.STORES, stores);
  }

  /**
   * Holt Läden aus dem Cache
   */
  async getCachedStores() {
    return await this.get(CACHE_KEYS.STORES);
  }

  /**
   * Fügt eine Operation zur Sync-Queue hinzu (für Offline-Änderungen)
   */
  async addToSyncQueue(operation) {
    try {
      const queue = await this.getSyncQueue();
      queue.push({
        ...operation,
        id: Date.now().toString(),
        timestamp: Date.now()
      });
      await AsyncStorage.setItem(CACHE_KEYS.SYNC_QUEUE, JSON.stringify(queue));
    } catch (error) {
      console.error('❌ Sync queue add error:', error);
    }
  }

  /**
   * Holt die Sync-Queue
   */
  async getSyncQueue() {
    try {
      const queue = await AsyncStorage.getItem(CACHE_KEYS.SYNC_QUEUE);
      return queue ? JSON.parse(queue) : [];
    } catch (error) {
      console.error('❌ Sync queue read error:', error);
      return [];
    }
  }

  /**
   * Entfernt eine Operation aus der Sync-Queue
   */
  async removeFromSyncQueue(operationId) {
    try {
      const queue = await this.getSyncQueue();
      const filtered = queue.filter(op => op.id !== operationId);
      await AsyncStorage.setItem(CACHE_KEYS.SYNC_QUEUE, JSON.stringify(filtered));
    } catch (error) {
      console.error('❌ Sync queue remove error:', error);
    }
  }

  /**
   * Speichert eine Offline-Änderung
   */
  async addOfflineChange(change) {
    try {
      const changes = await this.getOfflineChanges();
      changes.push({
        ...change,
        id: Date.now().toString(),
        timestamp: Date.now()
      });
      await AsyncStorage.setItem(CACHE_KEYS.OFFLINE_CHANGES, JSON.stringify(changes));
    } catch (error) {
      console.error('❌ Offline change save error:', error);
    }
  }

  /**
   * Holt alle Offline-Änderungen
   */
  async getOfflineChanges() {
    try {
      const changes = await AsyncStorage.getItem(CACHE_KEYS.OFFLINE_CHANGES);
      return changes ? JSON.parse(changes) : [];
    } catch (error) {
      console.error('❌ Offline changes get error:', error);
      return [];
    }
  }

  /**
   * Entfernt eine Offline-Änderung
   */
  async removeOfflineChange(changeId) {
    try {
      const changes = await this.getOfflineChanges();
      const filteredChanges = changes.filter(change => change.id !== changeId);
      await AsyncStorage.setItem(CACHE_KEYS.OFFLINE_CHANGES, JSON.stringify(filteredChanges));
    } catch (error) {
      console.error('❌ Offline change remove error:', error);
    }
  }

  /**
   * Löscht alle Offline-Änderungen (nach erfolgreichem Sync)
   */
  async clearOfflineChanges() {
    try {
      await AsyncStorage.removeItem(CACHE_KEYS.OFFLINE_CHANGES);
    } catch (error) {
      console.error('❌ Clear offline changes error:', error);
    }
  }

  /**
   * Leert die Sync-Queue
   */
  async clearSyncQueue() {
    try {
      await AsyncStorage.setItem(CACHE_KEYS.SYNC_QUEUE, JSON.stringify([]));
    } catch (error) {
      console.error('❌ Sync queue clear error:', error);
    }
  }

  /**
   * Speichert den Zeitstempel der letzten erfolgreichen Synchronisation
   */
  async setLastSync() {
    try {
      await AsyncStorage.setItem(CACHE_KEYS.LAST_SYNC, Date.now().toString());
    } catch (error) {
      console.error('❌ Last sync timestamp save error:', error);
    }
  }

  /**
   * Holt den Zeitstempel der letzten erfolgreichen Synchronisation
   */
  async getLastSync() {
    try {
      const timestamp = await AsyncStorage.getItem(CACHE_KEYS.LAST_SYNC);
      return timestamp ? parseInt(timestamp, 10) : null;
    } catch (error) {
      console.error('❌ Last sync timestamp read error:', error);
      return null;
    }
  }
}

export default new CacheService();

